import { useState, useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { getArticle, createArticle, updateArticle } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import ImageUploader from "@/components/ImageUploader";
import { ArrowLeft, Save, Eye, Plus, X, Lock, CalendarIcon } from "lucide-react";
import GradientBackground from "@/components/GradientBackground";
import { format } from "date-fns";
import { de } from "date-fns/locale";

const ADMIN_PASSWORD = process.env.REACT_APP_ADMIN_PASSWORD || "fallback";

function PasswordGate({ onSuccess }) {
  const [password, setPassword] = useState("");
  const [error, setError] = useState(false);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (password === ADMIN_PASSWORD) {
      sessionStorage.setItem("admin_auth", "true");
      onSuccess();
    } else {
      setError(true);
      setPassword("");
    }
  };

  return (
    <div className="min-h-screen text-white relative overflow-hidden flex items-center justify-center">
      <GradientBackground />
      <div className="relative z-10 w-full max-w-sm mx-4">
        <div className="bg-black/60 backdrop-blur-xl border-2 border-[#ff0090]/40 rounded-3xl p-8">
          <div className="text-center mb-8">
            <div className="w-16 h-16 rounded-full bg-[#ff0090]/20 border-2 border-[#ff0090] flex items-center justify-center mx-auto mb-4">
              <Lock className="w-8 h-8 text-[#ff0090]" />
            </div>
            <h1 className="font-['Outfit'] text-2xl font-bold text-white">Admin-Bereich</h1>
            <p className="text-white/60 text-sm mt-2">Bitte Codewort eingeben</p>
          </div>
          <form onSubmit={handleSubmit}>
            <Input
              type="password"
              value={password}
              onChange={(e) => { setPassword(e.target.value); setError(false); }}
              placeholder="Codewort..."
              className={`w-full bg-black/40 border-2 ${error ? 'border-red-500' : 'border-[#ff0090]/40'} rounded-xl text-white placeholder:text-white/40 h-12 mb-4`}
              autoFocus
            />
            {error && <p className="text-red-400 text-sm text-center mb-4">Falsches Codewort</p>}
            <Button type="submit" className="w-full h-12 bg-[#ff0090] hover:bg-[#ff0090]/80 text-white font-semibold rounded-xl">
              Einloggen
            </Button>
          </form>
          <div className="mt-6 text-center">
            <Link to="/" className="text-sm text-white/50 hover:text-white">← Zurück zur Startseite</Link>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function AdminArticleForm() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEdit = !!id;
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  useEffect(() => {
    const auth = sessionStorage.getItem("admin_auth");
    if (auth === "true") setIsAuthenticated(true);
  }, []);

  const [form, setForm] = useState({
    title: '',
    content: '',
    cover_image_url: '',
    tags: [],
    is_published: true,
    publish_date: new Date(),
  });
  const [tagInput, setTagInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!isAuthenticated) return;
    if (isEdit) {
      setLoading(true);
      getArticle(id).then(a => {
        setForm({
          title: a.title || '',
          content: a.content || '',
          cover_image_url: a.cover_image_url || '',
          tags: a.tags || [],
          is_published: a.is_published ?? true,
          publish_date: a.publish_date ? new Date(a.publish_date) : new Date(),
        });
        setLoading(false);
      }).catch(() => { toast.error("Artikel nicht gefunden"); navigate('/admin'); });
    }
  }, [id, isEdit, navigate, isAuthenticated]);

  // Show password gate if not authenticated
  if (!isAuthenticated) {
    return <PasswordGate onSuccess={() => setIsAuthenticated(true)} />;
  }

  const handleSave = async () => {
    if (!form.title.trim()) {
      toast.error("Titel ist erforderlich");
      return;
    }
    setSaving(true);
    try {
      const dataToSave = {
        ...form,
        publish_date: form.publish_date.toISOString(),
      };
      if (isEdit) {
        await updateArticle(id, dataToSave);
        toast.success("Artikel aktualisiert");
      } else {
        await createArticle(dataToSave);
        toast.success("Artikel erstellt");
      }
      navigate('/admin');
    } catch (e) {
      toast.error("Fehler beim Speichern");
    }
    setSaving(false);
  };

  const addTag = () => {
    const tag = tagInput.trim();
    if (tag && !form.tags.includes(tag)) {
      setForm(prev => ({ ...prev, tags: [...prev.tags, tag] }));
      setTagInput('');
    }
  };

  const removeTag = (tag) => {
    setForm(prev => ({ ...prev, tags: prev.tags.filter(t => t !== tag) }));
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" data-testid="article-form-loading">
        <div className="w-8 h-8 border-2 border-indigo-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50" data-testid="admin-article-form">
      {/* Header */}
      <header className="sticky top-0 z-40 bg-white border-b border-slate-200">
        <div className="max-w-4xl mx-auto px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button onClick={() => navigate('/admin')} className="text-slate-500 hover:text-slate-900 transition-colors" data-testid="article-form-back">
              <ArrowLeft className="w-5 h-5" />
            </button>
            <h1 className="font-['Outfit'] font-bold text-lg text-slate-900">
              {isEdit ? 'Artikel bearbeiten' : 'Neuer Artikel'}
            </h1>
          </div>
          <div className="flex items-center gap-3">
            {isEdit && (
              <Button variant="outline" size="sm" className="rounded-full gap-2" onClick={() => window.open(`/blog/${id}`, '_blank')} data-testid="preview-article-btn">
                <Eye className="w-4 h-4" /> Vorschau
              </Button>
            )}
            <Button onClick={handleSave} disabled={saving} className="btn-gradient rounded-full gap-2" data-testid="save-article-btn">
              <Save className="w-4 h-4" /> {saving ? 'Speichert...' : 'Speichern'}
            </Button>
          </div>
        </div>
      </header>

      <div className="max-w-4xl mx-auto px-6 py-8">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {/* Main Form */}
          <div className="md:col-span-2 space-y-6">
            <div className="bg-white rounded-2xl border border-slate-200 p-6 space-y-5">
              <div>
                <Label htmlFor="article-title" className="text-slate-700 font-semibold">Titel</Label>
                <Input
                  id="article-title"
                  value={form.title}
                  onChange={(e) => setForm(prev => ({ ...prev, title: e.target.value }))}
                  className="mt-2 rounded-xl text-lg font-semibold"
                  placeholder="Artikel-Titel"
                  data-testid="article-title-input"
                />
              </div>
              <div>
                <Label htmlFor="article-content" className="text-slate-700 font-semibold">Inhalt</Label>
                <Textarea
                  id="article-content"
                  value={form.content}
                  onChange={(e) => setForm(prev => ({ ...prev, content: e.target.value }))}
                  className="mt-2 rounded-xl"
                  placeholder="Schreibe deinen Artikel..."
                  rows={16}
                  data-testid="article-content-input"
                />
              </div>
            </div>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            <div className="bg-white rounded-2xl border border-slate-200 p-6 space-y-5">
              <div>
                <Label className="text-slate-700 font-semibold">Status</Label>
                <div className="flex items-center gap-3 mt-2">
                  <Switch
                    checked={form.is_published}
                    onCheckedChange={(v) => setForm(prev => ({ ...prev, is_published: v }))}
                    data-testid="article-published-switch"
                  />
                  <span className="text-sm text-slate-600">{form.is_published ? 'Veröffentlicht' : 'Entwurf'}</span>
                </div>
              </div>
              
              <div>
                <Label className="text-slate-700 font-semibold">Veröffentlichungsdatum</Label>
                <div className="mt-2">
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        className="w-full justify-start text-left font-normal rounded-xl"
                        data-testid="article-date-picker"
                      >
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {form.publish_date ? format(form.publish_date, "PPP", { locale: de }) : "Datum wählen"}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <Calendar
                        mode="single"
                        selected={form.publish_date}
                        onSelect={(date) => date && setForm(prev => ({ ...prev, publish_date: date }))}
                        initialFocus
                      />
                    </PopoverContent>
                  </Popover>
                </div>
              </div>

              <div>
                <Label className="text-slate-700 font-semibold">Titelbild</Label>
                <div className="mt-2">
                  <ImageUploader
                    value={form.cover_image_url}
                    onChange={(path) => setForm(prev => ({ ...prev, cover_image_url: path }))}
                  />
                </div>
              </div>
              <div>
                <Label className="text-slate-700 font-semibold">Tags</Label>
                <div className="flex gap-2 mt-2">
                  <Input
                    value={tagInput}
                    onChange={(e) => setTagInput(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addTag())}
                    placeholder="Tag hinzufügen"
                    className="rounded-xl flex-1"
                    data-testid="article-tag-input"
                  />
                  <Button variant="outline" size="icon" onClick={addTag} className="rounded-xl flex-shrink-0" data-testid="add-tag-btn">
                    <Plus className="w-4 h-4" />
                  </Button>
                </div>
                {form.tags.length > 0 && (
                  <div className="flex flex-wrap gap-2 mt-3" data-testid="article-tags-list">
                    {form.tags.map(tag => (
                      <Badge key={tag} variant="secondary" className="bg-indigo-50 text-indigo-600 border-none gap-1 pr-1">
                        {tag}
                        <button onClick={() => removeTag(tag)} className="ml-1 hover:text-red-500 transition-colors" data-testid={`remove-tag-${tag}`}>
                          <X className="w-3 h-3" />
                        </button>
                      </Badge>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
